import type {
  AnswerSelectedPayload,
  ContextualHelpSelectedPayload,
  EssentialSelectedPayload,
  PreferredAnswerNotSelectedPayload,
  QuestionAddedPayload,
  QuestionDeletedPayload,
  QuestionLimitReachedPayload,
  QuestionsDisplayedPayload,
  QuestionSelectedPayload,
  QuestionsSearchedPayload,
  SearchInteractedPayload,
  UnifiedQuestionLimitReachedPayload,
  UnifiedPreferredAnswerNotSelectedPayload,
  UnifiedQuestionsDisplayedPayload,
  UnifiedAnswerSelectedPayload,
  UnifiedQuestionSelectedPayload,
  UnifiedContextualHelpSelectedPayload,
  UnifiedSearchInteractedPayload,
  UnifiedQuestionsSearchedPayload,
  UnifiedEssentialSelectedPayload,
  UnifiedQuestionAddedPayload,
  UnifiedQuestionDeletedPayload,
  UnifiedCPQAQuestionsDisplayedPayload,
  UnifiedVerifyBoostSelectedPayload,
  UnifiedVerifyBoostNoQuestionSelectedPayload,
  UnifiedVerifyBoostContinuePayload,
} from './events';

/** Events generated from user interaction with Role Requirements */
export interface RoleRequirementsEvent {
  data: EventData[];
}

/** Event payload data */
export type AnalyticsPayload =
  | QuestionLimitReachedPayload
  | PreferredAnswerNotSelectedPayload
  | QuestionsDisplayedPayload
  | AnswerSelectedPayload
  | QuestionSelectedPayload
  | ContextualHelpSelectedPayload
  | SearchInteractedPayload
  | QuestionsSearchedPayload
  | EssentialSelectedPayload
  | QuestionAddedPayload
  | QuestionDeletedPayload;

export type UnifiedAnalyticsPayload =
  | UnifiedQuestionLimitReachedPayload
  | UnifiedCPQAQuestionsDisplayedPayload
  | UnifiedPreferredAnswerNotSelectedPayload
  | UnifiedQuestionsDisplayedPayload
  | UnifiedAnswerSelectedPayload
  | UnifiedQuestionSelectedPayload
  | UnifiedContextualHelpSelectedPayload
  | UnifiedSearchInteractedPayload
  | UnifiedQuestionsSearchedPayload
  | UnifiedEssentialSelectedPayload
  | UnifiedQuestionAddedPayload
  | UnifiedQuestionDeletedPayload
  | UnifiedVerifyBoostSelectedPayload
  | UnifiedVerifyBoostNoQuestionSelectedPayload
  | UnifiedVerifyBoostContinuePayload;

export enum EventType {
  QuestionSelected = 'questionSelected',
  QuestionUnselected = 'questionUnselected',
  QuestionsDisplayed = 'questionsDisplayed',
  OptionsSelected = 'optionsSelected',
  OptionsUnselected = 'optionsUnselected',
  HelpLinkSelected = 'helpLinkSelected',
  SearchInteracted = 'searchInteracted',
  QuestionsSearched = 'questionsSearched',
  MustHaveSelected = 'mustHaveSelected',
  MustHaveUnselected = 'mustHaveUnselected',
  QuestionAdded = 'questionAdded',
  QuestionDeleted = 'questionDeleted',
  Error = 'error',
}

export enum TrackingType {
  Link = 'link',
}

/** Name of event */
export enum LinkName {
  ClickOnQuestion = 'click_on_question',
  ClickOnAnswer = 'click_on_answer',
  JobHasRROptions = 'job_has_rr_options',
  ErrorMessageShown = 'error-message-shown',
  HelpLinkSelected = 'ecaja-shortlist-tooltip-expanded',
  SearchInteracted = 'rr_search_interacted',
  QuestionsSearched = 'rr_questions_searched',
  ClickOnMustHave = 'click_on_musthave_toggle',
  QuestionAdded = 'rr_add_question_interacted',
  QuestionDeleted = 'rr_delete_question_interacted',
}

export enum ObjectInteraction {
  QuestionSelected = 'rr_question_selected',
  QuestionUnselected = 'rr_question_unselected',
  AnswerSelected = 'rr_answer_selected',
  AnswerUnselected = 'rr_answer_unselected',
  ShortlistHelp = 'shortlist-help',
  MustHaveToggleSelected = 'rr-musthave-toggle-selected',
  MustHaveToggleUnselected = 'rr-musthave-toggle-unselected',
}

export enum AnalyticsEvent {
  QuestionSelected = 'rr-question-selected',
  QuestionUnselected = 'rr-question-unselected',
  QuestionsDisplayed = 'rr-questions-displayed',
  AnswerSelected = 'rr-answer-selected',
  AnswerUnselected = 'rr-answer-unselected',
  ErrorMessageShown = 'error-message-shown',
  FirstInteraction = 'rr-widget-first-interaction',
  ShortlistTooltipExpanded = 'ecaja-shortlist-tooltip-expanded',
  TooltipExpanded = 'tooltip-expanded',
  SearchInteracted = 'rr_search_interacted',
  QuestionsSearched = 'rr_questions_searched',
  MustHaveSelected = 'rr-musthave-toggle-selected',
  MustHaveUnselected = 'rr-musthave-toggle-unselected',
  QuestionAdded = 'rr_add_question_interacted',
  QuestionDeleted = 'rr_delete_question_interacted',
}

export enum UnifiedAnalyticsEvent {
  AnswerSelected = 'select_answer_pressed',
  AnswerUnselected = 'unselect_answer_pressed',
  TogglePressed = 'toggle_pressed',
  TooltipPressed = 'tooltip_pressed',
  QuestionSelected = 'select_question_pressed',
  QuestionUnselected = 'unselect_question_pressed',
  CustomQuestionAdded = 'add_custom_question_pressed',
  CustomQuestionDeleted = 'delete_custom_question_pressed',
  CPQAQuestionsDisplayed = 'cpqa_preselected_questions_displayed',
  QuestionsDisplayed = 'rr_questions_displayed',
  QuestionSearched = 'search_question_pressed',
  SearchInteracted = 'search_field_pressed',
  AlertViewed = 'alert_viewed',
  VerifyBoostSelected = 'rr_verified_boost_enable_pressed',
  VerifyBoostUnselected = 'rr_verified_boost_disable_pressed',
  VerifyBoostContinue = 'shortlist_continue_pressed',
}

export enum UnifiedAnalyticsMessageCode {
  QuestionLimitReached = 'rr_question_limit_reached',
  NoPreferredAnswers = 'rr_preferred_answer_not_selected',
  NoVerifyBoostQUestionSelected = 'rr_verifiable_question_not_selected',
}

export enum ToggleState {
  On = 'on',
  Off = 'off',
}

// toggle names need to be unique for the page. Prefix with rr_ to ensure uniqueness
export enum ToggleName {
  MustHave = 'rr_must_have',
}

// tooltip names need to be unique for the page. Prefix with rr_ to ensure uniqueness
export enum ToolTipName {
  ContextualHelp = 'rr_contextual_help',
  Certsy = 'rr_certsy',
}

export enum AnalyticsError {
  QuestionLimitReached = 'rr_question_limit_reached',
  PreferredAnswerNotSelected = 'rr_preferred_answer_not_selected',
}

export interface Payload<T extends TrackingType, L extends LinkName> {
  trackingType: T;
  linkName: L;
  eventName: AnalyticsEvent[];
  testingId?: string[];
}

export interface UnifiedPayload<T extends UnifiedAnalyticsEvent> {
  eventName: T;
  testingId?: string[];
  currentPageSection: 'role requirements';
}

export interface UnifiedAlertPayload<T extends UnifiedAnalyticsMessageCode>
  extends UnifiedPayload<UnifiedAnalyticsEvent.AlertViewed> {
  alertShown: UnifiedAlertShown<T>;
}

export interface UnifiedVerifyBoostAlertPayload<
  T extends UnifiedAnalyticsMessageCode,
> extends UnifiedAlertPayload<T> {
  roleReqQuestionnaireId: string;
  alertShown: UnifiedAlertShown<T>;
}

interface UnifiedAlertShown<T extends UnifiedAnalyticsMessageCode> {
  type: 'banner' | 'alert';
  title: string;
  message: string;
  messageCode: T;
  position?: string;
}

export interface EventData {
  name?: EventType;
  payload: {
    analytics: AnalyticsPayload | UnifiedAnalyticsPayload;
  };
}

export interface AnalyticalEvents {
  sendQuestionsDisplayedEvent: (
    questionnaireId: string,
    verifiableQuestionCount: number,
  ) => void;
  sendAnswerSelectionEvent: (
    answerId: string,
    questionId: string,
    questionnaireId: string,
    isSelectingAnswer: boolean,
    firstInteraction: boolean,
  ) => void;
  sendQuestionSelectionEvent: (
    questionId: string,
    questionnaireId: string,
    isSelected: boolean,
    firstInteraction: boolean,
  ) => void;
  sendEssentialSelectionEvent: (
    questionId: string,
    questionnaireId: string,
    isSelected: boolean,
    firstInteraction: boolean,
  ) => void;
  sendPreferredAnswerNotSelectedEvent: (message: string) => void;
  sendQuestionLimitReachedEvent: (message: string) => void;
  sendContextualHelpLinkSelectedEvent: (tooltipName: ToolTipName) => void;
  sendSearchInteractedEvent: () => void;
  sendQuestionsSearchedEvent: () => void;
  sendQuestionAddedEvent: () => void;
  sendQuestionDeletedEvent: () => void;
  sendCPQAQuestionsDisplayedEvent: () => void;
  sendVerifyBoostSelectionEvent: (
    questionnaireId: string,
    isSelected: boolean,
  ) => void;
  sendVerifyBoostNoQuestionSelectedEvent: (
    questionnaireId: string,
    message: string,
  ) => void;
  sendVerifyBoostContinueEvent: (
    questionnaireId: string,
    isVerifiedBoost: boolean,
  ) => void;
  setTestingId: (experiments: string[]) => void;
  resetTestingId: () => void;
}
